import React, { useEffect, useState } from 'react';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import HistoryChart from './HistoryChart';
import HistoryDays from './HistoryDays';
import ModalDeleteMeal from '../ModalDeleteMeal';
import { UserType, HistoryItemType, MealType } from '../../common/commonTypes';
import { getHistory, saveMeal } from '../../api';
import { groupMealsByDate } from './helpers';
import { makeMealsForSaving } from '../HomeTab/helpers';
import ModalSaveMeal from '../ModalSaveMeal';
import { isUserUnsubscribed } from '../../utils/helpers';
import { Trans, useTranslation } from 'react-i18next';
import HillTopBanner from '../HillTopBanner';

type HistoryTabComponentType = {
    user: UserType;
};

const HistoryTab = (props: HistoryTabComponentType) => {
    const { user } = props;
    const userIsUnsubscribed: boolean = isUserUnsubscribed(user);

    const [loading, setLoading] = useState<boolean>(true);
    const [groupedMeals, setGroupedMeals] = useState<HistoryItemType | null>(null);
    const [mealToEdit, setMealToEdit] = useState<MealType | null>(null);
    const [mealToDelete, setMealToDelete] = useState<MealType | null>(null);
    const [mealToFav, setMealToFav] = useState<MealType | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const { t, i18n } = useTranslation();

    const fetchHistory = async () => {
        try {
            const response = await getHistory(user.id);
            setGroupedMeals(groupMealsByDate(response.data.meals));
            setLoading(false);
        } catch (error) {
            alert(JSON.stringify(error)); // todo ERROR BANNER
            console.error('Error fetching History:', error);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory();
    }, []);

    const editMealHandler = async (meal: MealType) => {
        setMealToEdit(meal);
    };

    const deleteMealHandler = async (meal: MealType) => {
        setMealToDelete(meal);
    };

    const handleDeleteCancel = () => {
        setMealToDelete(null);
    };

    const handleEditCancel = () => {
        setMealToEdit(null);
    };

    const handleDeleteSuccess = () => {
        setSuccessMessage(`${mealToDelete?.name} ${t('SUCCESS_MSG_deleted')}`);
        setMealToDelete(null);
        fetchHistory();
    };

    const repeatMealHandler = async (meal: MealType) => {
        try {
            const confirmedMealsToSave = makeMealsForSaving([meal], user.id);
            await saveMeal(confirmedMealsToSave);
            setSuccessMessage(`${meal.name} ${t('SUCCESS_MSG_added_for_today')}`);
            fetchHistory();
        } catch (error) {
            console.error('Error submitting meal:', error);
            alert('Failed to add meal.'); // todo ERROR BANNER
        }
    };

    const favMealHandler = (meal: MealType) => {
        setMealToFav(meal);
    };

    const handleCancelSaving = () => {
        setMealToFav(null);
    };

    const handleSuccessSavingFav = () => {
        setSuccessMessage(`${mealToFav?.name} ${t('SUCCESS_MSG_added_to_favs')}`);
        setMealToFav(null);
    };

    const handleSuccessSavingEditedMeal = () => {
        setSuccessMessage(`${mealToEdit?.name} ${t('SUCCESS_MSG_saved')}`);
        setMealToEdit(null);
        fetchHistory();
    };

    return (
        <>
            {loading && (
                <div className="mt-40 text-center">
                    <CircularProgress color="primary" />
                </div>
            )}

            {!loading && !groupedMeals && (
                <div className="h-full flex flex-col items-center justify-center text-center text-gray-700 pb-[55px]">
                    <img
                        src="/avatar_200x200_history.png"
                        alt=""
                        className="block w-50 h-50 mb-4"
                    />
                    <div>
                        {t('hello')}, {user.firstName}, {t('you_didnt_log_last_week')}
                    </div>
                    <div className="text-sm mt-4">
                        <Trans i18nKey="go_to_today_tell_what_you_ate" />
                    </div>
                </div>
            )}

            {!loading && groupedMeals && (
                <div className="p-2">
                    {userIsUnsubscribed && <HillTopBanner height="tall" />}

                    {Object.keys(groupedMeals).length > 1 ? (
                        <HistoryChart groupedMeals={groupedMeals} />
                    ) : (
                        <div className="text-sm text-center text-gray-700 m-4">
                            {t('after_2_days_see_chart')}
                        </div>
                    )}

                    <HistoryDays
                        groupedMeals={groupedMeals}
                        onEditMeal={editMealHandler}
                        onDeleteMeal={deleteMealHandler}
                        onRepeatMeal={repeatMealHandler}
                        onFavMeal={favMealHandler}
                    />

                    {userIsUnsubscribed && <HillTopBanner height="short" />}

                    {mealToDelete && (
                        <ModalDeleteMeal
                            meal={mealToDelete}
                            actionType="HISTORY"
                            onCancel={handleDeleteCancel}
                            onSuccess={handleDeleteSuccess}
                        />
                    )}

                    {mealToEdit && (
                        <ModalSaveMeal
                            user={user}
                            meals={[mealToEdit]}
                            actionType="EDIT_MEAL"
                            onSuccess={handleSuccessSavingEditedMeal}
                            onCancel={handleEditCancel}
                        />
                    )}
                    {mealToFav && (
                        <ModalSaveMeal
                            user={user}
                            meals={[mealToFav]}
                            actionType="CREATE_FAV"
                            onSuccess={handleSuccessSavingFav}
                            onCancel={handleCancelSaving}
                        />
                    )}

                    {/* TODO: ABSTRACT, IMPROVE (err / success states) AND REUSE  */}
                    {successMessage && (
                        <Snackbar
                            // anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                            open
                            autoHideDuration={3000}
                            onClose={() => {
                                setSuccessMessage(null);
                            }}
                            message={successMessage}
                        />
                    )}
                </div>
            )}
        </>
    );
};

export default HistoryTab;
